import { Injectable } from '@angular/core';
import { CourseSubscriptionSession, CourseSubscriptionSessionProvider, SectionContentSession } from '@stuplay';
import { from, Observable, of, timer } from 'rxjs';
import { catchError, concatMap, last, map, switchMap, takeWhile } from 'rxjs/operators';
import { ToastService } from '../components/toast/toast.service';
import { NavigationProvider } from './navigation.provider';
import { OrderedService } from './ordered.service';
import { StatsService } from './stats.service';
import { StorageService } from './storage.service';

/**
 * Paso común al salir de una actividad cuyo progreso llega de forma asíncrona
 * (SCORM Cloud lo reporta a la API por webhook, no desde el front).
 *
 * Espera a que la actividad quede completada, refresca la sesión del curso para que
 * el sidebar y el bloqueo de módulos ordenados la reflejen, y avanza a la siguiente
 * actividad. Si la siguiente está bloqueada, se queda y avisa.
 */
@Injectable({ providedIn: 'root' })
export class ActivityExitCoordinator {
    // Esperas sucesivas antes de cada relectura de la sesión (~7,5 s en total).
    private static readonly PROGRESS_POLL_DELAYS = [500, 1000, 2000, 4000];

    constructor(
        private navigationProvider: NavigationProvider,
        private orderedService: OrderedService,
        private statsService: StatsService,
        private storageService: StorageService,
        private toastService: ToastService,
        private courseSubscriptionSessionProvider: CourseSubscriptionSessionProvider,
    ) { }

    /**
     * Emite true si navegó a la siguiente actividad y false si se quedó en la actual.
     */
    exitAndAdvance(sectionContentSession: SectionContentSession, preview: boolean): Observable<boolean> {
        return this.waitForCompletion(sectionContentSession).pipe(
            switchMap((session) => this.markDoneIfCompleted(session, preview)),
            switchMap(() => this.reloadCourseSession(preview)),
            map(() => this.advance(sectionContentSession)),
        );
    }

    private waitForCompletion(session: SectionContentSession): Observable<SectionContentSession> {
        if (!session || session.doneAt) {
            return of(session);
        }

        return from(ActivityExitCoordinator.PROGRESS_POLL_DELAYS).pipe(
            concatMap((delay) => timer(delay).pipe(switchMap(() => session.get()))),
            takeWhile((current) => !current.doneAt, true),
            last(),
            catchError(() => of(session)),
        );
    }

    /**
     * Respaldo heredado: si la API ya da el 100 % pero sin fecha de completado, se la pone.
     */
    private markDoneIfCompleted(session: SectionContentSession, preview: boolean): Observable<unknown> {
        if (preview || !session || session.doneAt || session.progress !== 100) {
            return of(null);
        }

        return session.update(true, this.storageService.get('company')?.id).pipe(catchError(() => of(null)));
    }

    private reloadCourseSession(preview: boolean): Observable<CourseSubscriptionSession> {
        const course = this.navigationProvider.currentCourse;

        return this.courseSubscriptionSessionProvider.getOrCreateCourseSubscriptionSession(course?.id, preview).pipe(
            map((courseSubscriptionSession) => {
                this.navigationProvider.currentCourseSubscriptionSession = courseSubscriptionSession;
                this.orderedService.setSession(courseSubscriptionSession);
                this.orderedService.setSection(course?.sections || []);
                this.statsService.refresh(courseSubscriptionSession);

                return courseSubscriptionSession;
            }),
            catchError(() => of(null)),
        );
    }

    private advance(sectionContentSession: SectionContentSession): boolean {
        // El alumno pudo haberse ido a otra actividad mientras se esperaba el progreso.
        if (this.navigationProvider.currentSectionContent?.id !== sectionContentSession?.sectionContentId) {
            return false;
        }

        if (!this.navigationProvider.canNavigateWithinCourse()) {
            return false;
        }

        if (!this.navigationProvider.isNextSectionAvailable()) {
            this.toastService.push('content-locked', 'warning');
            return false;
        }

        document.getElementById('view')?.scroll?.(0, 0);
        this.navigationProvider.goRoute('next');

        return true;
    }
}
